import type { MutableRefObject } from 'react';
import React from 'react';

import { useBasketCTAButton } from 'src/components/molecules/BasketCTAButton/useBasketCTAButton';
import { usePriceBreakdown } from 'src/components/molecules/PriceBreakdown/usePriceBreakdown';
import { useQuantitySelector } from 'src/components/molecules/QuantitySelector/useQuantitySelector';
import { ExpressCheckoutLazy } from 'src/components/organisms/ExpressCheckout/ExpressCheckoutLazy';
import { PersonalisationContainer } from 'src/components/organisms/Personalisation/containers/PersonalisationContainer';
import type { MediaItem } from 'src/components/organisms/ProductMediaCarousel/types';
import { DRAWER_ID_SUFFIX } from './ProductOptionsDrawerFooter';
import { ProductOptionsDrawerView } from './ProductOptionsDrawerView';

export interface ProductOptionsDrawerStateProps {
  heading: string | null;
  isOpen: boolean;
  media: MediaItem[];
  showValidationErrors: boolean;
}

export interface ProductOptionsDrawerDispatchProps {
  onClose: () => void;
  onVisible: () => void;
}

interface ProductOptionsDrawerOwnProps {
  target: MutableRefObject<HTMLDivElement | null>;
}

type ProductOptionsDrawerProps = ProductOptionsDrawerStateProps &
  ProductOptionsDrawerDispatchProps &
  ProductOptionsDrawerOwnProps;

/**
 * Binds the drawer's redux state to its presentational view.
 *
 * The hooks are called here rather than in PDPLayout so that the whole subtree stays behind the
 * layout's `product_options_drawer` dynamic import. Composing them a level up would pull it all
 * into the main PDP bundle.
 */
export const ProductOptionsDrawer = ({
  heading,
  isOpen,
  media,
  onClose,
  onVisible,
  showValidationErrors,
  target,
}: ProductOptionsDrawerProps) => {
  const basketCtaProps = useBasketCTAButton();
  const priceBreakdown = usePriceBreakdown();
  const quantitySelectorProps = useQuantitySelector();

  return (
    <ProductOptionsDrawerView
      basketCta={basketCtaProps}
      /* Reveals itself only once every required option is answered, so the wallets cannot
         skip the configuration the shopper came here to give; with nothing required they
         show straight away. */
      expressCheckout={<ExpressCheckoutLazy idSuffix={DRAWER_ID_SUFFIX} inline />}
      heading={heading}
      isOpen={isOpen}
      media={media}
      onClose={onClose}
      onVisible={onVisible}
      personalisation={<PersonalisationContainer displayValidation={showValidationErrors} />}
      priceBreakdown={priceBreakdown}
      quantitySelector={quantitySelectorProps}
      target={target}
    />
  );
};
